import * as ApiTypes from '@pillar-portal/api-types';
import type { CloverBrowserTokenizationFetch } from '@pillar-portal/api-types';
import type { CardBrand, CardFormData } from '../../card-payment-entry';
import { resolveCardBrand, validateCardFormData } from '../../card-payment-entry';

export type { CloverBrowserTokenizationFetch } from '@pillar-portal/api-types';

export interface CloverCardFormTokenizationInput {
    readonly cardData: CardFormData;
    readonly ecommerceApiKey: string;
    readonly ecommerceTokenizationUrl: string;
    readonly fetchImplementation?: CloverBrowserTokenizationFetch;
}

function resolveCloverBrowserCardBrandName(
    cardNumber: string
): ApiTypes.CloverBrowserCardBrandName {
    const cardBrand: CardBrand = resolveCardBrand(cardNumber);
    switch (cardBrand) {
        case 'Visa':
            return 'Visa';
        case 'Mastercard':
            return 'Mastercard';
        case 'AmEx':
            return 'AmEx';
        case 'Discover':
            return 'Discover';
        case 'Diners Club':
            return 'Diners Club';
        case 'JCB':
            return 'JCB';
        case 'Unknown':
            throw new Error('Clover card tokenization requires a supported card brand.');
    }
}

function buildSharedCloverBrowserTokenizationInput(input: {
    readonly cardData: CardFormData;
    readonly ecommerceApiKey: string;
    readonly ecommerceTokenizationUrl: string;
    readonly fetchImplementation?: CloverBrowserTokenizationFetch;
}): ApiTypes.CloverBrowserTokenizationInput {
    const validationError: string | null = validateCardFormData(input.cardData);
    if (validationError !== null) {
        throw new Error(`Clover card tokenization requires valid card data: ${validationError}`);
    }

    const ecommerceApiKey: string = input.ecommerceApiKey.trim();
    if (ecommerceApiKey.length === 0) {
        throw new Error('Clover card tokenization requires an Ecommerce API key.');
    }

    return {
        cardData: {
            ...input.cardData,
            cardBrand: resolveCloverBrowserCardBrandName(input.cardData.cardNumber),
        },
        ecommerceApiKey,
        ecommerceTokenizationUrl: input.ecommerceTokenizationUrl,
        ...(input.fetchImplementation === undefined
            ? {}
            : { fetchImplementation: input.fetchImplementation }),
    };
}

export async function tokenizeCardWithCloverEcommerce(
    input: CloverCardFormTokenizationInput
): Promise<ApiTypes.CloverEcommerceBrowserTokenizationResultV2> {
    const sharedInput: ApiTypes.CloverBrowserTokenizationInput =
        buildSharedCloverBrowserTokenizationInput(input);
    return await ApiTypes.tokenizeCardWithCloverEcommerce(sharedInput);
}
